import { useLocalState, useMyUser } from '@consigli/hooks';
import React, { createContext, useCallback, useContext, useMemo } from 'react';

type DemoModeContextValue = {
  demoModeEnabled: boolean;
  setDemoModeEnabled: (enabled: boolean) => void;
  toggleDemoMode: () => void;
  demoView: boolean | undefined;
  adminFeaturesEnabled: boolean;
};

const DemoModeContext = createContext<DemoModeContextValue | undefined>(undefined);

export const DemoModeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { user } = useMyUser();
  const [demoModeEnabled, setDemoModeEnabled] = useLocalState<boolean>(
    'consigli_demo_mode_enabled',
    false,
  );

  const toggleDemoMode = useCallback(() => {
    setDemoModeEnabled(!demoModeEnabled);
  }, [demoModeEnabled, setDemoModeEnabled]);

  const demoView = useMemo(
    () => (user.isSuperuser && demoModeEnabled ? true : undefined),
    [user.isSuperuser, demoModeEnabled],
  );

  const adminFeaturesEnabled = useMemo(
    () => user.isSuperuser && !demoModeEnabled,
    [user.isSuperuser, demoModeEnabled],
  );

  const value = useMemo(
    () => ({ demoModeEnabled, setDemoModeEnabled, toggleDemoMode, demoView, adminFeaturesEnabled }),
    [demoModeEnabled, setDemoModeEnabled, toggleDemoMode, demoView, adminFeaturesEnabled],
  );

  return <DemoModeContext.Provider value={value}>{children}</DemoModeContext.Provider>;
};

export const useDemoMode = (): DemoModeContextValue => {
  const ctx = useContext(DemoModeContext);
  if (!ctx) {
    throw new Error('useDemoMode must be used within a DemoModeProvider');
  }
  return ctx;
};
